import {
  ExpressionSpecification,
  FilterSpecification,
  LayerProps,
} from 'shared/map-exports';
import { WeatherStationArrowIcon } from 'features/mapping/icons';
import {
  METERS_PER_SEC_TO_MPH_FACTOR,
  OFFLINE_STATION_COLOR,
  SENSOR_TYPE_TO_COLOR_STOPS,
  TEMPERATURE_COLOR_STOPS,
} from 'features/weatherStations/weatherStations.constants';
import { SELECTED_FEATURE_HIGHLIGHT_COLOR } from 'features/mapping/mapping.constants';
import { FocusedSensorType } from 'state/useWeatherStationState';
import { TFunction } from 'i18next';
import { TOOLTIP_LABEL_LAYOUT_STYLES } from 'features/mapping/mapping.styles';
import {
  ATTRIBUTE_CONFIGS,
  BASE_CIRCLE_RADIUS,
  MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
  MIN_ICON_ZOOM_LEVEL,
  MIN_LABEL_ZOOM_LEVEL,
  SOURCE_LAYER,
} from './WeatherStationsLayer.constants';
import { SnakeCaseAttributeKey } from './WeatherStationsLayer.types';
import { getValueDisplayExpression } from './WeatherStationsLayer.utils';

const weatherStationAttributeSnakeCase: Record<
  FocusedSensorType,
  SnakeCaseAttributeKey
> = {
  airTemp: 'air_temp',
  dewPointTemperature: 'dew_point_temperature',
  precipitation1Hr: 'precipitation_1hr',
  relativeHumidity: 'relative_humidity',
  windGust: 'wind_gust',
  windSpeed: 'wind_speed',
};

const withinCoverageAreaFilter: FilterSpecification = [
  '==',
  ['get', 'is_within_coverage_area'],
  true,
];

const isOfflineFilter: FilterSpecification = [
  '==',
  ['get', 'is_offline'],
  true,
];

const isOnlineFilter: FilterSpecification = ['!', isOfflineFilter];

export const getPointIconStyle = (
  isInternalUser: boolean,
  focusedSensorType: FocusedSensorType,
): LayerProps => {
  const snakeCaseAttribute =
    weatherStationAttributeSnakeCase[focusedSensorType];
  const colorStops =
    SENSOR_TYPE_TO_COLOR_STOPS[focusedSensorType] || TEMPERATURE_COLOR_STOPS;
  const conversionExpression = getValueDisplayExpression(snakeCaseAttribute);

  const style: LayerProps = {
    type: 'circle',
    minzoom: MIN_ICON_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    paint: {
      'circle-color': [
        'case',
        [
          'any',
          ['!=', ['typeof', ['get', snakeCaseAttribute]], 'number'],
          isOfflineFilter,
        ],
        OFFLINE_STATION_COLOR, // fallback color for undefined/null/non-numeric values or offline stations
        [
          'interpolate',
          ['linear'],
          conversionExpression,
          ...colorStops.flatMap((stop) => [stop.value, stop.color]),
        ],
      ],
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_ICON_ZOOM_LEVEL,
        BASE_CIRCLE_RADIUS,
        MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
        6,
      ],
      'circle-stroke-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_ICON_ZOOM_LEVEL,
        1.25,
        MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
        2,
      ],
      'circle-stroke-color': '#000000',
    },
  };
  if (!isInternalUser) {
    style.filter = withinCoverageAreaFilter;
  }
  return style;
};

const hasWindDirectionFilter: FilterSpecification = ['has', 'wind_direction'];

const validWindDirectionFilter: FilterSpecification = [
  '!=',
  ['get', 'wind_direction'],
  null,
];

const arrowIconFilter: FilterSpecification = [
  'all',
  isOnlineFilter,
  hasWindDirectionFilter,
  validWindDirectionFilter,
];

export const getArrowIconStyle = (isInternalUser: boolean): LayerProps => {
  const filter: FilterSpecification = [...arrowIconFilter];
  if (!isInternalUser) {
    filter.push(withinCoverageAreaFilter);
  }
  const style: LayerProps = {
    type: 'symbol',
    minzoom: MIN_ICON_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    layout: {
      'icon-image': WeatherStationArrowIcon.name,
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_ICON_ZOOM_LEVEL,
        0.625,
        MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
        1.125,
      ],
      'icon-allow-overlap': true,
      'icon-ignore-placement': false,
      'icon-rotation-alignment': 'map',
      // Synoptic wind direction is the direction the wind is coming FROM, so reverse it
      'icon-rotate': ['%', ['+', ['get', 'wind_direction'], 180], 360],
    },
    filter,
  };
  return style;
};

const getMphNumberExpression = (propKey: string): ExpressionSpecification => [
  'case',
  ['==', ['get', propKey], 0], // Handles the case of exactly 0
  '0',
  // Check if the fractional part is zero after conversion
  ['==', ['%', ['*', ['get', propKey], METERS_PER_SEC_TO_MPH_FACTOR], 1], 0],
  [
    'to-string',
    ['round', ['*', ['get', propKey], METERS_PER_SEC_TO_MPH_FACTOR]],
  ], // Convert m/s to mph, round, then convert to string
  // Otherwise, format with one decimal place
  [
    'number-format',
    ['*', ['get', propKey], METERS_PER_SEC_TO_MPH_FACTOR],
    { 'min-fraction-digits': 0, 'max-fraction-digits': 1 },
  ],
];

export const getDynamicLabelStyle = (
  selectedAttribute: FocusedSensorType,
  isInternalUser: boolean,
  t: TFunction,
): LayerProps => {
  const config = ATTRIBUTE_CONFIGS[selectedAttribute];

  const hasValidValueFilter: FilterSpecification = [
    '!=',
    ['get', config.propKey],
    null,
  ];

  const noDataFilter: FilterSpecification = [
    'all',
    isOnlineFilter,
    [
      'any',
      ['==', ['get', 'is_observation_recent'], false],
      ['!', hasValidValueFilter],
    ],
  ];

  /**
   * Display the label if:
   * 1. Sensor is offline (shows "Offline" text)
   * 2. Sensor has no data/stale data (shows "No data" text)
   * 3. Sensor has a valid value (shows actual data)
   */
  const sensorTypeFilter: FilterSpecification = [
    'any',
    isOfflineFilter,
    noDataFilter,
    hasValidValueFilter,
  ];

  const filter: FilterSpecification = isInternalUser
    ? sensorTypeFilter
    : ['all', sensorTypeFilter, withinCoverageAreaFilter];

  // If the selected attribute is wind_speed, just show that
  const isWindSpeed = config.propKey === 'wind_speed';

  return {
    type: 'symbol',
    minzoom: MIN_LABEL_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    layout: {
      'text-offset': ['case', arrowIconFilter, [0, 2.75], [0, 2]],
      ...TOOLTIP_LABEL_LAYOUT_STYLES,
      'text-field': [
        'case',
        isOfflineFilter,
        ['format', ['literal', t('common.offline')]],
        noDataFilter,
        ['format', ['literal', t('common.noData')]],
        isWindSpeed
          ? [
              'format',
              // 1. Icon
              ['image', config.iconName],
              ['literal', ' '],
              // 2. Wind Speed Value
              getMphNumberExpression('wind_speed'),
              // 3. Unit
              ['literal', ` ${config.unit}`],
              { 'font-scale': 0.85 },
            ]
          : [
              'case',
              ['!=', ['get', 'wind_speed'], null],
              [
                'format',
                // 1. Wind Speed
                getMphNumberExpression('wind_speed'),
                ['literal', ' mph'],
                { 'font-scale': 0.85 },
                // 2. Separator
                ['literal', '  | '],
                { 'text-color': '#E5E5E5' },
                // 3. Icon
                ['image', config.iconName],
                ['literal', ' '],
                // 4. Attribute Value
                getValueDisplayExpression(config.propKey, 'string'),
                // 5. Unit
                ['literal', ` ${config.unit}`],
                { 'font-scale': 0.85 },
              ],
              [
                'format',
                // 1. Icon
                ['image', config.iconName],
                ['literal', ' '],
                // 2. Attribute Value
                getValueDisplayExpression(config.propKey, 'string'),
                // 3. Unit
                ['literal', ` ${config.unit}`],
                { 'font-scale': 0.85 },
              ],
            ],
      ],
    },
    paint: {
      'text-color': '#000000', // Ensures text is black
      'icon-color': '#ffffff', // Ensures the background tooltip is white
    },
    filter,
  };
};

export const getSelectedPointIconStyle = (
  isInternalUser: boolean,
  selectedStationId?: number,
): LayerProps => {
  const selectedStationFilter: FilterSpecification = [
    '==',
    ['id'],
    selectedStationId ?? null,
  ];
  const filter: FilterSpecification = isInternalUser
    ? selectedStationFilter
    : ['all', withinCoverageAreaFilter, selectedStationFilter];

  return {
    type: 'circle',
    minzoom: MIN_ICON_ZOOM_LEVEL,
    'source-layer': SOURCE_LAYER,
    paint: {
      'circle-radius': 0,
      'circle-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
      'circle-blur': 1.25,
    },
    filter,
  };
};
